/**
 * HUB-G — Modèle ActivityEvent + mappers + merge timeline unifiée.
 */
import { ACTIVITE_TIMELINE_OVERVIEW_LIMIT } from './activityTabs';
import { MOTIF_LABELS, movementTypeMeta } from '../stock/stockDisplay';
import {
  getStripeOrderLabel,
  getSumupOrderLabel,
  stripeStatusMeta,
  sumupStatusMeta,
} from '../paiements/paiementsDisplay';
import { statusBadgeClass } from '../reparations/reparationsDisplay';

export const EVENT_TYPES = {
  VENTE: 'vente',
  RETRAIT: 'retrait',
  PAIEMENT_SUMUP: 'paiement_sumup',
  PAIEMENT_STRIPE: 'paiement_stripe',
  CAISSE_OPEN: 'caisse_open',
  CAISSE_CLOSE: 'caisse_close',
  STOCK: 'stock',
  REPARATION: 'reparation',
};

export const TIMELINE_TYPE_FILTERS = [
  { key: 'all', label: 'Tous' },
  { key: EVENT_TYPES.VENTE, label: 'Ventes' },
  { key: EVENT_TYPES.RETRAIT, label: 'Retraits' },
  { key: 'paiements', label: 'Paiements' },
  { key: 'caisse', label: 'Caisse' },
  { key: EVENT_TYPES.STOCK, label: 'Stock' },
  { key: EVENT_TYPES.REPARATION, label: 'Réparations' },
];

export const EVENT_TYPE_META = {
  [EVENT_TYPES.VENTE]: { label: 'Vente', badge: 'rm-badge rm-badge--success' },
  [EVENT_TYPES.RETRAIT]: { label: 'Retrait', badge: 'rm-badge rm-badge--danger' },
  [EVENT_TYPES.PAIEMENT_SUMUP]: { label: 'Paiement SumUp', badge: 'rm-badge rm-badge--muted' },
  [EVENT_TYPES.PAIEMENT_STRIPE]: { label: 'Paiement Stripe', badge: 'rm-badge rm-badge--muted' },
  [EVENT_TYPES.CAISSE_OPEN]: { label: 'Ouverture caisse', badge: 'rm-badge rm-badge--success' },
  [EVENT_TYPES.CAISSE_CLOSE]: { label: 'Fermeture caisse', badge: 'rm-badge rm-badge--muted' },
  [EVENT_TYPES.STOCK]: { label: 'Stock', badge: 'rm-badge rm-badge--muted' },
  [EVENT_TYPES.REPARATION]: { label: 'Réparation', badge: 'rm-badge rm-badge--muted' },
};

export function magasinScopeLabel(magasinId) {
  if (!magasinId || magasinId === 'ALL') return 'Tous les magasins';
  return magasinId.toUpperCase();
}

export function formatEventDate(value) {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleString('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function isInDateRange(isoDate, dateDebut, dateFin) {
  if (!dateDebut && !dateFin) return true;
  const d = new Date(isoDate);
  if (Number.isNaN(d.getTime())) return !dateDebut && !dateFin;
  const day = d.toISOString().slice(0, 10);
  if (dateDebut && day < dateDebut) return false;
  if (dateFin && day > dateFin) return false;
  return true;
}

function buildDeepLink(tab, params = {}) {
  const qs = new URLSearchParams({ tab });
  Object.entries(params).forEach(([k, v]) => {
    if (v != null && v !== '') qs.set(k, String(v));
  });
  return `/activite?${qs.toString()}`;
}

export function mapVenteToEvent(vente) {
  const isRetrait = vente.type === 'retrait';
  const type = isRetrait ? EVENT_TYPES.RETRAIT : EVENT_TYPES.VENTE;
  const meta = EVENT_TYPE_META[type];
  return {
    id: `vente-${vente.ref_vente}`,
    type,
    at: vente.date_vente,
    label: vente.designation || vente.ref_vente,
    detail: vente.ref_vente,
    amount: Number(vente.prix_total ?? 0),
    magasin_id: vente.magasin_id || 'M1',
    user: vente.vendeur,
    status: vente.statut_paiement,
    typeLabel: meta.label,
    badge: meta.badge,
    deepLink: `/activite?tab=ventes&search=${encodeURIComponent(vente.ref_vente)}`,
    sourceTab: 'ventes',
  };
}

export function mapSumupToEvent(payment) {
  const meta = EVENT_TYPE_META[EVENT_TYPES.PAIEMENT_SUMUP];
  const statusMeta = sumupStatusMeta(payment.status);
  return {
    id: `sumup-${payment.checkout_id}`,
    type: EVENT_TYPES.PAIEMENT_SUMUP,
    at: payment.updated_at || payment.created_at,
    label: getSumupOrderLabel(payment),
    detail: payment.checkout_id,
    amount: Number(payment.amount ?? 0),
    magasin_id: payment.magasin_id || 'M1',
    user: payment.confirmed_by,
    status: statusMeta.label,
    typeLabel: meta.label,
    badge: meta.badge,
    deepLink: buildDeepLink('paiements', { search: payment.checkout_id || payment.device_ref || '' }),
    sourceTab: 'paiements',
  };
}

export function mapStripeToEvent(payment) {
  const meta = EVENT_TYPE_META[EVENT_TYPES.PAIEMENT_STRIPE];
  const statusMeta = stripeStatusMeta(payment.status);
  return {
    id: `stripe-${payment.stripe_session_id || payment.id}`,
    type: EVENT_TYPES.PAIEMENT_STRIPE,
    at: payment.updated_at || payment.created_at,
    label: getStripeOrderLabel(payment),
    detail: payment.stripe_session_id || payment.id,
    amount: Number(payment.amount ?? 0),
    magasin_id: payment.magasin_id || 'M1',
    user: payment.confirmed_by,
    status: statusMeta.label,
    typeLabel: meta.label,
    badge: meta.badge,
    deepLink: buildDeepLink('paiements', { search: payment.device_ref || payment.stripe_session_id || '' }),
    sourceTab: 'paiements',
  };
}

export function mapCaisseSessionsToEvents(sessions = []) {
  const events = [];
  sessions.forEach((s) => {
    const mag = s.magasin_id || 'M1';
    if (s.heure_ouverture) {
      const meta = EVENT_TYPE_META[EVENT_TYPES.CAISSE_OPEN];
      events.push({
        id: `caisse-open-${s.id}`,
        type: EVENT_TYPES.CAISSE_OPEN,
        at: s.heure_ouverture,
        label: `Session caisse ${s.date_caisse}`,
        detail: s.opened_by || s.user_ouverture,
        amount: null,
        magasin_id: mag,
        user: s.opened_by || s.user_ouverture,
        status: 'Ouverte',
        typeLabel: meta.label,
        badge: meta.badge,
        deepLink: '/caisse-historique',
        sourceTab: 'caisse',
      });
    }
    if (s.heure_fermeture) {
      const meta = EVENT_TYPE_META[EVENT_TYPES.CAISSE_CLOSE];
      events.push({
        id: `caisse-close-${s.id}`,
        type: EVENT_TYPES.CAISSE_CLOSE,
        at: s.heure_fermeture,
        label: `Clôture caisse ${s.date_caisse}`,
        detail: s.closed_by || s.user_fermeture,
        amount: Number(s.total_ventes ?? 0),
        magasin_id: mag,
        user: s.closed_by || s.user_fermeture,
        status: s.force_closed ? 'Fermée (forcée)' : 'Fermée',
        typeLabel: meta.label,
        badge: meta.badge,
        deepLink: '/caisse-historique',
        sourceTab: 'caisse',
      });
    }
  });
  return events;
}

export function mapStockMovementToEvent(m) {
  const typeMeta = movementTypeMeta(m);
  const meta = EVENT_TYPE_META[EVENT_TYPES.STOCK];
  const isTransfer = m.type_mouvement === 'transfert';
  return {
    id: `stock-${m.id}`,
    type: EVENT_TYPES.STOCK,
    at: m.created_at,
    label: `${typeMeta.label} — ${m.designation || m.reference}`,
    detail: MOTIF_LABELS[m.motif] || m.motif,
    amount: m.quantite,
    magasin_id: isTransfer ? (m.from_magasin_id || 'M1') : (m.magasin_id || 'M1'),
    magasin_id_to: isTransfer ? (m.to_magasin_id || 'M2') : null,
    user: m.admin_username,
    status: typeMeta.label,
    typeLabel: meta.label,
    badge: meta.badge,
    deepLink: m.article_id ? `/stock/modifier/${m.article_id}` : '/stock/mouvements',
    sourceTab: 'stock',
  };
}

export function mapRepairToEvent(device) {
  const meta = EVENT_TYPE_META[EVENT_TYPES.REPARATION];
  return {
    id: `repair-${device.ref}`,
    type: EVENT_TYPES.REPARATION,
    at: device.updated_at || device.created_at,
    label: `${device.ref} — ${device.model || 'Appareil'}`,
    detail: device.problem,
    amount: null,
    magasin_id: device.magasin_id || 'M1',
    user: device.technician_name,
    status: device.status,
    typeLabel: meta.label,
    badge: statusBadgeClass(device.status),
    deepLink: `/reparations/${device.ref}`,
    sourceTab: 'reparations',
  };
}

export function matchesEventSearch(event, search) {
  if (!search?.trim()) return true;
  const q = search.trim().toLowerCase();
  const haystack = [
    event.label,
    event.detail,
    event.user,
    event.status,
    event.magasin_id,
    event.typeLabel,
    event.amount != null ? String(event.amount) : '',
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
  return haystack.includes(q);
}

export function matchesEventMagasin(event, magasinId) {
  if (!magasinId || magasinId === 'ALL') return true;
  return (event.magasin_id || 'M1').toUpperCase() === magasinId.toUpperCase();
}

export function matchesEventVendeur(event, vendeur) {
  if (!vendeur) return true;
  if (event.type !== EVENT_TYPES.VENTE && event.type !== EVENT_TYPES.RETRAIT) return true;
  return (event.user || '').toLowerCase() === vendeur.toLowerCase();
}

export function matchesTimelineTypeFilter(event, filterKey) {
  if (!filterKey || filterKey === 'all') return true;
  if (filterKey === 'paiements') {
    return event.type === EVENT_TYPES.PAIEMENT_SUMUP || event.type === EVENT_TYPES.PAIEMENT_STRIPE;
  }
  if (filterKey === 'caisse') {
    return event.type === EVENT_TYPES.CAISSE_OPEN || event.type === EVENT_TYPES.CAISSE_CLOSE;
  }
  return event.type === filterKey;
}

export function mergeActivityEvents({
  ventes = [],
  sumupPayments = [],
  stripePayments = [],
  caisseSessions = [],
  stockMovements = [],
  repairs = [],
  filters = {},
  typeFilter = 'all',
  limit = ACTIVITE_TIMELINE_OVERVIEW_LIMIT,
} = {}) {
  const raw = [
    ...ventes.map(mapVenteToEvent),
    ...sumupPayments.map(mapSumupToEvent),
    ...stripePayments.map(mapStripeToEvent),
    ...mapCaisseSessionsToEvents(caisseSessions),
    ...stockMovements.map(mapStockMovementToEvent),
    ...repairs.map(mapRepairToEvent),
  ];

  const seen = new Set();
  return raw
    .filter((e) => e.at && isInDateRange(e.at, filters.date_debut, filters.date_fin))
    .filter((e) => matchesEventMagasin(e, filters.magasin_id))
    .filter((e) => matchesEventSearch(e, filters.search))
    .filter((e) => matchesEventVendeur(e, filters.vendeur))
    .filter((e) => matchesTimelineTypeFilter(e, typeFilter))
    .filter((e) => {
      if (seen.has(e.id)) return false;
      seen.add(e.id);
      return true;
    })
    .sort((a, b) => {
      const diff = new Date(b.at) - new Date(a.at);
      if (diff !== 0) return diff;
      return a.id.localeCompare(b.id);
    })
    .slice(0, limit);
}

export function computeTimelineKpis(events = []) {
  const counts = {
    total: events.length,
    vente: 0,
    retrait: 0,
    paiement: 0,
    caisse: 0,
    stock: 0,
    reparation: 0,
    M1: 0,
    M2: 0,
  };
  events.forEach((e) => {
    const mag = (e.magasin_id || 'M1').toUpperCase();
    if (counts[mag] != null) counts[mag] += 1;
    if (e.type === EVENT_TYPES.VENTE) counts.vente += 1;
    else if (e.type === EVENT_TYPES.RETRAIT) counts.retrait += 1;
    else if (e.type === EVENT_TYPES.PAIEMENT_SUMUP || e.type === EVENT_TYPES.PAIEMENT_STRIPE) counts.paiement += 1;
    else if (e.type === EVENT_TYPES.CAISSE_OPEN || e.type === EVENT_TYPES.CAISSE_CLOSE) counts.caisse += 1;
    else if (e.type === EVENT_TYPES.STOCK) counts.stock += 1;
    else if (e.type === EVENT_TYPES.REPARATION) counts.reparation += 1;
  });
  return counts;
}
